import { useEffect, useRef, useState } from "react";
import { Editor, useEditorState } from "@tiptap/react";
import styled from "styled-components";
import { Button, Divider, Dropdown, Input, Popover, Select, Tooltip, message } from "antd";
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ClearOutlined,
  FontColorsOutlined,
  HighlightOutlined,
  ItalicOutlined,
  LinkOutlined,
  LineOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { postEditorImage } from "../../api/postAPI";
import { BACKGROUND_COLORS, DEFAULT_FONT_SIZE, FONT_SIZES, PaletteColor, TEXT_COLORS } from "./freeTextConfig";

interface Props {
  editor: Editor;
  postId: number;
}

const BLOCK_TYPES = [
  { value: "paragraph", label: "본문" },
  { value: "h1", label: "제목 1" },
  { value: "h2", label: "제목 2" },
  { value: "h3", label: "제목 3" },
  { value: "h4", label: "제목 4" },
];

// 사이트 상단 고정 헤더의 아래쪽 위치 (화면 크기마다 높이가 다르고, 스크롤하면 숨었다 나타난다)
const useFixedHeaderBottom = (): number => {
  const [bottom, setBottom] = useState(0);

  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;
    const update = () => setBottom(Math.max(0, Math.round(header.getBoundingClientRect().bottom)));
    update();
    // 헤더가 접히고 펴지는 동안에도 따라가도록 크기 변화를 지켜본다
    const observer = new ResizeObserver(update);
    observer.observe(header);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return bottom;
};

const FreeTextToolbar = ({ editor, postId }: Props) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const headerBottom = useFixedHeaderBottom();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkInput, setLinkInput] = useState("");

  // v3 는 트랜잭션마다 다시 그리지 않으므로 툴바 상태는 필요한 값만 구독한다
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      blockType: e.isActive("heading", { level: 1 })
        ? "h1"
        : e.isActive("heading", { level: 2 })
          ? "h2"
          : e.isActive("heading", { level: 3 })
            ? "h3"
            : e.isActive("heading", { level: 4 })
              ? "h4"
              : "paragraph",
      fontSize: (e.getAttributes("textStyle").fontSize as string | undefined) ?? DEFAULT_FONT_SIZE,
      color: e.getAttributes("textStyle").color as string | undefined,
      backgroundColor: e.getAttributes("textStyle").backgroundColor as string | undefined,
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      strike: e.isActive("strike"),
      alignCenter: e.isActive({ textAlign: "center" }),
      alignRight: e.isActive({ textAlign: "right" }),
      bulletList: e.isActive("bulletList"),
      orderedList: e.isActive("orderedList"),
      link: e.isActive("link"),
      inTable: e.isActive("table"),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const chain = () => editor.chain().focus();

  const changeBlockType = (value: string) => {
    if (value === "paragraph") chain().setParagraph().run();
    else chain().toggleHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 | 4 }).run();
  };

  const changeFontSize = (value: string) => {
    if (value === DEFAULT_FONT_SIZE) chain().unsetFontSize().run();
    else chain().setFontSize(value).run();
  };

  const applyTextColor = (color: PaletteColor) => {
    if (color.clear || !color.value) chain().unsetColor().run();
    else chain().setColor(color.value).run();
  };

  const applyBackgroundColor = (color: PaletteColor) => {
    if (color.clear || !color.value) chain().unsetBackgroundColor().run();
    else chain().setBackgroundColor(color.value).run();
  };

  const openLink = (open: boolean) => {
    if (open) setLinkInput((editor.getAttributes("link").href as string | undefined) ?? "");
    setLinkOpen(open);
  };

  const applyLink = () => {
    const href = linkInput.trim();
    if (!href) {
      chain().extendMarkRange("link").unsetLink().run();
    } else if (!/^(https?:\/\/|mailto:)/i.test(href)) {
      message.warning("http://, https:// 또는 mailto: 로 시작하는 주소를 입력해주세요.");
      return;
    } else {
      chain().extendMarkRange("link").setLink({ href }).run();
    }
    setLinkOpen(false);
  };

  const uploadImage = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      message.warning("이미지 파일만 올릴 수 있습니다.");
      return;
    }
    const result = await postEditorImage(postId, file);
    const url = result?.file?.url;
    if (!result?.success || !url) {
      message.error("이미지 업로드에 실패했습니다.");
      return;
    }
    chain().setImage({ src: url, alt: file.name }).run();
  };

  const tableMenuItems = [
    { key: "addRowBefore", label: "위에 행 추가", onClick: () => chain().addRowBefore().run() },
    { key: "addRowAfter", label: "아래에 행 추가", onClick: () => chain().addRowAfter().run() },
    { key: "addColumnBefore", label: "왼쪽에 열 추가", onClick: () => chain().addColumnBefore().run() },
    { key: "addColumnAfter", label: "오른쪽에 열 추가", onClick: () => chain().addColumnAfter().run() },
    { type: "divider" as const },
    { key: "toggleHeaderRow", label: "머리글 행 켜기/끄기", onClick: () => chain().toggleHeaderRow().run() },
    { type: "divider" as const },
    { key: "deleteRow", label: "행 삭제", danger: true, onClick: () => chain().deleteRow().run() },
    { key: "deleteColumn", label: "열 삭제", danger: true, onClick: () => chain().deleteColumn().run() },
    { key: "deleteTable", label: "표 삭제", danger: true, onClick: () => chain().deleteTable().run() },
  ];

  const palette = (colors: PaletteColor[], current: string | undefined, onPick: (color: PaletteColor) => void) => (
    <PaletteGrid>
      {colors.map((color) => (
        <Tooltip key={color.name} title={color.name}>
          <Swatch
            type="button"
            $color={color.value}
            $active={color.clear ? !current : current?.toLowerCase() === color.value?.toLowerCase()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(color)}
          >
            {color.clear && "×"}
          </Swatch>
        </Tooltip>
      ))}
    </PaletteGrid>
  );

  const toolButton = (title: string, icon: React.ReactNode, onClick: () => void, active = false, disabled = false) => (
    <Tooltip title={title}>
      <Button
        size="small"
        type={active ? "primary" : "text"}
        ghost={active}
        icon={icon}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
      />
    </Tooltip>
  );

  return (
    // 글이 길어져도 메뉴가 화면 위(헤더 바로 아래)에 붙어 따라온다
    <Bar style={{ top: headerBottom }}>
      {toolButton("실행 취소", <UndoOutlined />, () => chain().undo().run(), false, !state.canUndo)}
      {toolButton("다시 실행", <RedoOutlined />, () => chain().redo().run(), false, !state.canRedo)}
      <Divider type="vertical" />

      <Select size="small" value={state.blockType} options={BLOCK_TYPES} onChange={changeBlockType} style={{ width: 88 }} />
      <Tooltip title="글자 크기">
        <Select
          size="small"
          value={state.fontSize}
          onChange={changeFontSize}
          style={{ width: 76 }}
          options={FONT_SIZES.map((size) => ({ value: size, label: parseFloat(size) }))}
        />
      </Tooltip>
      <Divider type="vertical" />

      {toolButton("굵게 (Ctrl+B)", <BoldOutlined />, () => chain().toggleBold().run(), state.bold)}
      {toolButton("기울임 (Ctrl+I)", <ItalicOutlined />, () => chain().toggleItalic().run(), state.italic)}
      {toolButton("밑줄 (Ctrl+U)", <UnderlineOutlined />, () => chain().toggleUnderline().run(), state.underline)}
      {toolButton("취소선", <StrikethroughOutlined />, () => chain().toggleStrike().run(), state.strike)}

      <Popover trigger="click" content={palette(TEXT_COLORS, state.color, applyTextColor)} title="글자 색">
        <Tooltip title="글자 색">
          <Button size="small" type="text" onMouseDown={(e) => e.preventDefault()}>
            <ColorIcon $color={state.color ?? "#1f1f1f"}>
              <FontColorsOutlined />
            </ColorIcon>
          </Button>
        </Tooltip>
      </Popover>
      <Popover trigger="click" content={palette(BACKGROUND_COLORS, state.backgroundColor, applyBackgroundColor)} title="배경 색">
        <Tooltip title="배경 색">
          <Button size="small" type="text" onMouseDown={(e) => e.preventDefault()}>
            <ColorIcon $color={state.backgroundColor ?? "transparent"} $background>
              <HighlightOutlined />
            </ColorIcon>
          </Button>
        </Tooltip>
      </Popover>
      <Divider type="vertical" />

      {toolButton("왼쪽 정렬", <AlignLeftOutlined />, () => chain().setTextAlign("left").run(), !state.alignCenter && !state.alignRight)}
      {toolButton("가운데 정렬", <AlignCenterOutlined />, () => chain().setTextAlign("center").run(), state.alignCenter)}
      {toolButton("오른쪽 정렬", <AlignRightOutlined />, () => chain().setTextAlign("right").run(), state.alignRight)}
      <Divider type="vertical" />

      {toolButton("글머리 기호 목록", <UnorderedListOutlined />, () => chain().toggleBulletList().run(), state.bulletList)}
      {toolButton("번호 목록", <OrderedListOutlined />, () => chain().toggleOrderedList().run(), state.orderedList)}
      {toolButton("구분선", <LineOutlined />, () => chain().setHorizontalRule().run())}
      <Divider type="vertical" />

      <Popover
        trigger="click"
        open={linkOpen}
        onOpenChange={openLink}
        title="링크"
        content={
          <LinkForm>
            <Input
              size="small"
              placeholder="https://"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              onPressEnter={applyLink}
              autoFocus
            />
            <Button size="small" type="primary" onClick={applyLink}>
              적용
            </Button>
          </LinkForm>
        }
      >
        <Tooltip title="링크 (빈 값으로 적용하면 해제)">
          <Button size="small" type={state.link ? "primary" : "text"} ghost={state.link} icon={<LinkOutlined />} />
        </Tooltip>
      </Popover>

      {state.inTable ? (
        <Dropdown menu={{ items: tableMenuItems }} trigger={["click"]}>
          <Tooltip title="표 편집">
            <Button size="small" type="primary" ghost icon={<TableOutlined />} onMouseDown={(e) => e.preventDefault()} />
          </Tooltip>
        </Dropdown>
      ) : (
        toolButton("표 넣기 (3×3)", <TableOutlined />, () => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())
      )}

      {toolButton("이미지 넣기", <PictureOutlined />, () => imageInputRef.current?.click())}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          void uploadImage(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <Divider type="vertical" />

      {toolButton("서식 지우기", <ClearOutlined />, () => chain().unsetAllMarks().clearNodes().run())}
    </Bar>
  );
};

const Bar = styled.div`
  position: sticky;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);

  .ant-divider-vertical {
    margin: 0 4px;
  }
`;

const PaletteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 24px);
  gap: 6px;
`;

const Swatch = styled.button<{ $color?: string; $active: boolean }>`
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 4px;
  border: ${(p) => (p.$active ? "2px solid #1677ff" : "1px solid #d9d9d9")};
  background: ${(p) => p.$color ?? "#fff"};
  color: #8c8c8c;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
`;

const ColorIcon = styled.span<{ $color: string; $background?: boolean }>`
  display: inline-flex;
  padding: 0 2px;
  border-bottom: 3px solid ${(p) => p.$color};
  border-radius: 1px;
  background: ${(p) => (p.$background ? p.$color : "transparent")};
`;

const LinkForm = styled.div`
  display: flex;
  gap: 6px;
  width: 280px;
`;

export default FreeTextToolbar;
